import React, { memo, useEffect, useRef, useState } from 'react';
import { getAvatarImage, PLUS_ICON_SMALL } from '../../pages/Wellness/WellnessDesign';

// 친구 아바타 컴포넌트 (동적 import)
const FriendAvatar = memo(({ friendId, friendName }) => {
    const [avatarSrc, setAvatarSrc] = useState(null);
    const AVATAR_COUNT = 6;
    
    useEffect(() => {
        if (!friendId) return;
        
        const index = friendId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % AVATAR_COUNT;
        
        getAvatarImage(index)
            .then(src => {
                if (src) setAvatarSrc(src);
            })
            .catch(error => {
                console.error('아바타 이미지 로드 실패:', error);
            });
    }, [friendId]);
    
    return (
        <div
            className="friend-icon"
            style={{ backgroundColor: '#E8E8E8' }}
        >
            {avatarSrc ? (
                <img
                    src={avatarSrc}
                    alt={friendName}
                    className="friend-avatar-image"
                    loading="lazy"
                    decoding="async"
                />
            ) : (
                <div className="friend-avatar-placeholder" style={{ width: '100%', height: '100%', backgroundColor: '#E8E8E8' }} />
            )}
        </div>
    );
});

FriendAvatar.displayName = 'FriendAvatar';

const FriendList = memo(({
    realFriends,
    isButtonMoving,
    onPlusClick,
    onFriendClick,
    isFriendDataLoaded = false,
    shouldBlink = false,
    showRedDot = false,
    isRedDotAppearing = false,
}) => {
    const containerRef = useRef(null);
    const touchRef = useRef({ startX: 0, startY: 0, isHorizontal: null });

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onTouchStart = (e) => {
            touchRef.current = {
                startX: e.touches[0].clientX,
                startY: e.touches[0].clientY,
                isHorizontal: null,
                scrollLeft: el.scrollLeft,
            };
        };

        const onTouchMove = (e) => {
            const { startX, startY } = touchRef.current;
            const dx = e.touches[0].clientX - startX;
            const dy = e.touches[0].clientY - startY;

            if (touchRef.current.isHorizontal === null) {
                touchRef.current.isHorizontal = Math.abs(dx) > Math.abs(dy);
            }

            if (touchRef.current.isHorizontal) {
                e.preventDefault();
                e.stopPropagation();
                el.scrollLeft = touchRef.current.scrollLeft - dx;
            }
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className="wellness-top-header"
            style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                touchAction: 'pan-y',
                WebkitOverflowScrolling: 'touch',
                overscrollBehaviorX: 'contain',
                position: 'relative',
                whiteSpace: 'nowrap',
                width: '100%',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingRight: '160px',
                boxSizing: 'border-box',
                pointerEvents: 'auto',
            }}
        >
            {realFriends.map((friend, index) => (
                <div
                    key={friend.user_id || index}
                    className="friend-item"
                    style={{
                        cursor: 'pointer',
                        opacity: isFriendDataLoaded ? 1 : 0,
                        transition: 'opacity 0.25s ease',
                    }}
                    onClick={() => onFriendClick?.(friend)}
                >
                    <FriendAvatar friendId={friend.user_id} friendName={friend.friend_name} />
                    <span className="friend-name">{friend.friend_name}</span>
                </div>
            ))}

            {/* 친구추가 버튼 */}
            <div
                className={`friend-add-button ${isButtonMoving ? 'moving' : ''} ${isRedDotAppearing ? 'red-dot-appearing' : ''}`}
                onClick={onPlusClick}
                style={{ 
                    position: 'relative',
                    cursor: isButtonMoving ? 'not-allowed' : 'pointer'
                }}
            >
                <div className="friend-add-icon">
                    <img src={PLUS_ICON_SMALL} alt="추가" className="plus-icon" loading="lazy" decoding="async" />
                </div>
                <span className="friend-add-text">친구 추가</span>

                {showRedDot && (
                    <div
                        className={`red-notification-dot ${isRedDotAppearing ? 'red-dot-fade-in' : ''}`}
                        style={{
                            position: 'absolute',
                            top: '2px',
                            right: '8px',
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#FF4444',
                            borderRadius: '50%',
                            animation: isRedDotAppearing ? 'redDotFadeIn 0.8s ease-out forwards, pulse-red 1.5s infinite 0.8s' : 'pulse-red 1.5s infinite',
                            zIndex: 101,
                            border: '1px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}
                    />
                )}
            </div>
        </div>
    );
});

export default FriendList;
